import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil, Sort } from '@factorialco/f0-react/icons/app'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  CompetencyCompetencyLevelFragment,
  CompetencyLevelsDataCollectionDocument,
} from 'generated/resources/graphql'
import { CompetencyLevelFragment } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { CompetencyLevelDeleteModal } from 'modules/competencies/components/CompetencyLevelDeleteModal'
import { CompetencyLevelEditModal } from 'modules/competencies/components/CompetencyLevelEditModal'
import { CompetencyLevelsCreateSidepanel } from 'modules/competencies/components/CompetencyLevelsCreateSidepanel'

interface Props {
  competency: {
    id: number
    name: string
    competencyLevels: CompetencyCompetencyLevelFragment[]
  }
  canManageCompetencyLevels?: boolean
  onReorderLevels?: () => void
}

export const CompetencyLevelsDataCollection = ({
  competency,
  canManageCompetencyLevels = false,
  onReorderLevels,
}: Props) => {
  const [levelsQuantity, setLevelsQuantity] = useState(0)
  const [editLevel, setEditLevel] = useState<CompetencyLevelFragment | null>(null)
  const [deleteLevel, setDeleteLevel] = useState<CompetencyLevelFragment | null>(null)

  const [showCreateSidepanel, setShowCreateSidepanel] = useState(false)

  const handleAdd = () => {
    setShowCreateSidepanel(true)
  }

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: CompetencyLevelsDataCollectionDocument,
    mapData: (data) => {
      return data.competencies.competencyLevelsConnection.nodes || []
    },
    mapPagination: (data) => {
      const totalCount = data.competencies.competencyLevelsConnection.totalCount || 0
      setLevelsQuantity(totalCount)
      return {
        total: data.competencies.competencyLevelsConnection.totalCount || 0,
      }
    },
    mapVariables: ({ search }) => {
      return {
        competencyIds: [competency.id],
        search,
      }
    },
    search: {
      enabled: true,
    },
    itemActions: (item: CompetencyLevelFragment) => {
      if (!canManageCompetencyLevels) {
        return []
      }

      return [
        {
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: () => setEditLevel(item),
        },
        {
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => setDeleteLevel(item),
          critical: true,
        },
      ]
    },
    primaryActions: () =>
      canManageCompetencyLevels
        ? {
            label: i18n.t('competencies_v3.levels.data_collection.actions.create'),
            icon: Add,
            onClick: () => handleAdd(),
          }
        : undefined,
    secondaryActions: {
      expanded: 0,
      actions: () =>
        canManageCompetencyLevels && levelsQuantity > 1
          ? [
              {
                label: i18n.t('competencies_v3.levels.data_collection.actions.reorder'),
                icon: Sort,
                onClick: () => onReorderLevels?.(),
              },
            ]
          : [],
    },
    perPage: 20,
  })

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: [
          {
            label: i18n.t('competencies_v3.levels.data_collection.columns.order'),
            render: (item: { position: number }) => ({
              type: 'text' as const,
              value: (item.position + 1).toString(),
            }),
            width: 100,
          },
          {
            label: i18n.t('competencies_v3.levels.data_collection.columns.name'),
            render: (item: { name: string }) => ({
              type: 'text' as const,
              value: item.name,
            }),
          },
          {
            label: i18n.t('competencies_v3.levels.data_collection.columns.description'),
            render: (item: { description: string }) => ({
              type: 'longText' as const,
              value: {
                text: item.description || '-',
                full: true,
              },
            }),
          },
        ],
        frozenColumns: 2 as const,
      },
    },
  ]

  const emptyStates = {
    'no-data': {
      title: i18n.t('competencies_v3.levels.data_collection.empty_state.title'),
      description: i18n.t('competencies_v3.levels.data_collection.empty_state.description'),
      actions: canManageCompetencyLevels
        ? [
            {
              label: i18n.t('competencies_v3.levels.data_collection.actions.create'),
              variant: 'outline' as const,
              icon: Add,
              onClick: () => handleAdd(),
            },
          ]
        : [],
    },
  }

  return (
    <>
      <OneDataCollection
        fullHeight
        source={source}
        visualizations={visualizations}
        emptyStates={emptyStates}
      />

      {editLevel && (
        <CompetencyLevelEditModal
          level={editLevel}
          onClose={() => setEditLevel(null)}
          onSuccess={() => setEditLevel(null)}
        />
      )}

      {deleteLevel && (
        <CompetencyLevelDeleteModal
          level={deleteLevel}
          onClose={() => setDeleteLevel(null)}
          onSuccess={() => {
            setDeleteLevel(null)
            setLevelsQuantity(Math.max(levelsQuantity - 1, 0))
          }}
        />
      )}

      <CompetencyLevelsCreateSidepanel
        competencyId={competency.id}
        open={showCreateSidepanel}
        existingLevelsCount={levelsQuantity}
        onSuccess={() => setShowCreateSidepanel(false)}
        onClose={() => setShowCreateSidepanel(false)}
        existingLevels={competency.competencyLevels}
      />
    </>
  )
}
